import { format, parseISO } from 'date-fns';

import type {
  CustomPacingInput,
  CustomPacingWindowInput,
  LineItemMetrics,
  LineItemV5,
  SaveCustomPacingPayloadInput,
  SaveCustomPacingWindowInput,
} from '../../../../../apis/graphql';
import { removeTypeNamesFromObject } from '../../../../../apis/graphql';
import { getOldCacheData } from '../../../../../common/utils';
import { CacheEntityIdentifier } from '../../../../../configs/cache';
import { LINE_ITEM_TRAFFICKING_COLUMNS } from '../../../TraffickingPage/hooks/fragments';
import {
  AFFECTED_LINE_ITEMS_LIMIT_REACHED,
  CUSTOM_PACING_DATE_FORMAT,
  CUSTOM_PACING_MAX_AFFECT_LINE_ITEM_LIMIT,
  DEFAULT_HEAVY_UP,
} from '../constants';
import type { CPScheduleDateRange, SetCPScheduleFormValues } from '../types';
import { CpScheduleFormFields } from '../types';

const formatCustomPacingDate = (value: string | null): string | null => {
  if (!value) return null;

  const date = parseISO(value);

  return format(date, CUSTOM_PACING_DATE_FORMAT);
};

const formatCustomPacingWindowDates = (
  start: string | null,
  end: string | null,
  index: number,
  rangesLength: number,
  isDynamicStartDateEnabled: boolean,
  isDynamicEndDateEnabled: boolean
): Pick<CustomPacingWindowInput, 'startDate' | 'endDate'> => {
  return {
    startDate: index === 0 && isDynamicStartDateEnabled ? null : formatCustomPacingDate(start),
    endDate: index === rangesLength - 1 && isDynamicEndDateEnabled ? null : formatCustomPacingDate(end),
  };
};

export const mapScheduleFormToCustomPacingInput = (values: SetCPScheduleFormValues): CustomPacingInput => ({
  windows: values[CpScheduleFormFields.Ranges].map(
    (range, index): CustomPacingWindowInput => ({
      id: index + 1,
      heavyUp: range.heavyUp ?? DEFAULT_HEAVY_UP,
      ...formatCustomPacingWindowDates(
        range.start,
        range.end,
        index,
        values[CpScheduleFormFields.Ranges].length,
        values.isDynamicStartDateEnabled,
        values.isDynamicEndDateEnabled
      ),
    })
  ),
  timezone: values[CpScheduleFormFields.Timezone],
});

export const constructSaveCustomPacingInput = (
  lineItemList: LineItemMetrics[],
  ranges: CPScheduleDateRange[],
  timezone: string,
  isDynamicStartDateEnabled: boolean,
  isDynamicEndDateEnabled: boolean
): SaveCustomPacingPayloadInput => {
  return {
    lineItems: lineItemList.map((li) => removeTypeNamesFromObject(li)),
    customPacing: {
      windows: ranges.map(
        (range, index): SaveCustomPacingWindowInput => ({
          id: index + 1,
          ...formatCustomPacingWindowDates(
            range.start,
            range.end,
            index,
            ranges.length,
            isDynamicStartDateEnabled,
            isDynamicEndDateEnabled
          ),
        })
      ),
      timezone,
    },
  };
};

export const getLineItemsToRefetchForCacheUpdate = (lineItemsIdList: string[]): string[] => {
  return lineItemsIdList.filter((lineItemId) => {
    const { cacheData } = getOldCacheData<LineItemV5>({
      cacheIdPrefix: CacheEntityIdentifier.LineItemV5,
      id: lineItemId,
      fragment: LINE_ITEM_TRAFFICKING_COLUMNS,
    });

    return !!cacheData;
  });
};

export const getCalculationErrors = (lineItems: LineItemMetrics[]): string | null => {
  if (lineItems.length > CUSTOM_PACING_MAX_AFFECT_LINE_ITEM_LIMIT) {
    return AFFECTED_LINE_ITEMS_LIMIT_REACHED;
  }

  return null;
};
